import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from 'Components/atoms';
import { useAmpyChat } from 'Rdx/contexts/AmpyChatContext';
import AnalyticsAmpyChatPanel from 'Components/pages/AnalyticsDashboardPage/AnalyticsAmpyChatPanel';
import { useAskAmpyEnabled } from 'Hooks/useAskAmpyEnabled';
import './AmpyChatSidebar.scss';

const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.5;
const DEFAULT_WIDTH = 420;
const WIDTH_STORAGE_KEY = 'ampy_chat_sidebar_width';
export type AmpyChatSidebarSide = 'left' | 'right';

interface AmpyChatSidebarProps {
    side?: AmpyChatSidebarSide;
    showFloatingToggle?: boolean;
}

const getStoredWidth = (): number => {
    try {
        const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY);
        if (raw) {
            const parsed = parseInt(raw, 10);
            if (!isNaN(parsed) && parsed >= MIN_WIDTH) {
              return parsed;
            }
        }
    } catch {
        // ignore
    }
    return DEFAULT_WIDTH;
};

const SparkleIcon = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" fill="currentColor" />
    </svg>
);

export const AmpyChatSidebar = ({ side = 'right', showFloatingToggle = true }: AmpyChatSidebarProps) => {
    const { messages, pending, pendingAcknowledgement, error, isOpen, isUnavailable, isContextRefreshDisabled, sendMessage, clearChat, toggleOpen } = useAmpyChat();
    const { isAskAmpyEnabled, isLoading } = useAskAmpyEnabled();
    const [width, setWidth] = useState(getStoredWidth);
    const isDragging = useRef(false);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const isLeft = side === 'left';

    const clampWidth = useCallback((w: number) => {
        const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
        return Math.max(MIN_WIDTH, Math.min(w, maxWidth));
    }, []);

    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            isDragging.current = true;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            const startX = e.clientX;
            const startWidth = width;

            const onMouseMove = (moveEvent: MouseEvent) => {
                if (!isDragging.current) {
                  return;
                }
                const delta = isLeft ? moveEvent.clientX - startX : startX - moveEvent.clientX;
                const newWidth = clampWidth(startWidth + delta);
                setWidth(newWidth);
            };

            const onMouseUp = () => {
                isDragging.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                setWidth((current) => {
                    try {
                        window.localStorage.setItem(WIDTH_STORAGE_KEY, String(current));
                    } catch {
                        // ignore
                    }
                    return current;
                });
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [width, clampWidth, isLeft],
    );

    useEffect(() => {
        const handleResize = () => {
            setWidth((prev) => clampWidth(prev));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [clampWidth]);

    if (isLoading || !isAskAmpyEnabled) {
        return null;
    }

    if (!isOpen && showFloatingToggle) {
        return (
            <button
                type="button"
                className={`ampy-chat-toggle ampy-chat-toggle--${side}`}
                onClick={toggleOpen}
                data-testid="ampy-chat-toggle"
                title="Ask Ampy"
            >
                <span className="ampy-chat-toggle__icon">
                    <SparkleIcon size={18} />
                </span>
                <span className="ampy-chat-toggle__label">Ask Ampy</span>
            </button>
        );
    }
    if (!isOpen) {
        return null;
    }

    return (
        <div
            ref={sidebarRef}
            className={`ampy-chat-sidebar ampy-chat-sidebar--${side}`}
            style={{ width }}
            data-testid="ampy-chat-sidebar"
        >
            <div
                className="ampy-chat-sidebar__resize-handle"
                onMouseDown={onMouseDown}
                data-testid="ampy-chat-resize-handle"
            />
            <div className="ampy-chat-sidebar__header">
                <div className="ampy-chat-sidebar__header-title">
                    <span className="ampy-chat-sidebar__header-icon">
                        <SparkleIcon size={18} />
                    </span>
                    <Typography variant="h4" color="text-high-emphasis">
                        Ask Ampy
                    </Typography>
                </div>
                <div className="ampy-chat-sidebar__header-actions">
                    <button
                        type="button"
                        className={[
                            'ampy-chat-sidebar__action-btn',
                            isContextRefreshDisabled ? 'ampy-chat-sidebar__action-btn--disabled' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={clearChat}
                        title={isUnavailable ? 'Ask Ampy is currently unavailable, please try again later' : 'Clear chat'}
                        disabled={isContextRefreshDisabled}
                        data-testid="ampy-chat-clear"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="ampy-chat-sidebar__action-btn"
                        onClick={toggleOpen}
                        title="Close chat"
                        data-testid="ampy-chat-collapse"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {isLeft ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
                        </svg>
                    </button>
                </div>
            </div>
            <div className="ampy-chat-sidebar__body">
                <AnalyticsAmpyChatPanel
                    messages={messages}
                    pending={pending}
                    pendingAcknowledgement={pendingAcknowledgement}
                    error={error}
                    onSend={sendMessage}
                    showHeader={false}
                />
            </div>
        </div>
    );
};

export default AmpyChatSidebar;
